import Team from "./Team";
import TeamRow from "./TeamRow";
import TeamToolbar from "./TeamToolbar";
import { mobileWidth, tabletWidth } from "../../utilis/screenWidthUtils.ts";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import {PhoneHQLoader} from 'ch3-ui-lib';
import {selectUserId} from "../../store/features/Auth/authSlice";
import {filterTeam, groupTeam, sortTeam, TeamFilter, TeamMember} from "./teamFilters";
import {useTeamMembers} from "./useTeamMembers";
import {StatusDot} from "./TeamMemberParts";
import {groupLabel} from "./teamFormat";
import {readTeamViewPrefs, TeamViewPrefs, writeTeamViewPrefs} from "./teamViewPrefs";
import {useRtcFeatures} from "../../features/groupCalls/hooks/useGroupCall";
import {TeamSelectionBar} from "../../features/callSurface/team/TeamSelectionBar";

const TeamList = () => {
    const userId = useSelector(selectUserId)

    const {members: teamMembers, isLoading} = useTeamMembers();

    const [filter, setFilter] = useState<TeamFilter>('ALL');
    const [query, setQuery] = useState('');
    const [prefs, setPrefs] = useState<TeamViewPrefs>(() => readTeamViewPrefs(userId));
    const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
    const rtcFeatures = useRtcFeatures();
    const selectable = rtcFeatures.groupCalls;
    const selectedMembers = useMemo(() => teamMembers.filter(member => selected.has(String(member.userId))), [selected, teamMembers]);
    const toggleSelected = (member: TeamMember) => setSelected(current => {
        const key = String(member.userId);
        const next = new Set(current);
        if (next.has(key)) {
            next.delete(key);
        } else {
            next.add(key);
        }
        return next;
    });

    useEffect(() => {
        setPrefs(readTeamViewPrefs(userId));
    }, [userId]);

    const updatePrefs = (next: TeamViewPrefs) => {
        setPrefs(next);
        writeTeamViewPrefs(userId, next);
    };

    const visibleMembers = useMemo(
        () => sortTeam(filterTeam(teamMembers, filter, query), prefs.sort),
        [teamMembers, filter, query, prefs.sort],
    );

    const sections = useMemo(
        () => prefs.grouped ? groupTeam(visibleMembers) : [{group: null, members: visibleMembers}],
        [visibleMembers, prefs.grouped],
    );

    if (isLoading) {
        return (
            <div className={`flex w-full `}>
                <PhoneHQLoader/>
            </div>


        );
    }

    const renderMembers = (members: TeamMember[]) => prefs.view === 'grid' ? (
        <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
            {members.map(member => (
                <Team
                    key={String(member.userId)}
                    user={member}
                    selected={selected.has(String(member.userId))}
                    onToggleSelected={selectable ? () => toggleSelected(member) : undefined}
                />
            ))}
        </div>
    ) : (
        <div className={'divide-y overflow-hidden rounded-xl border bg-surface border-line border-line'}>
            {members.map(member => (
                <TeamRow
                    key={String(member.userId)}
                    user={member}
                    selected={selected.has(String(member.userId))}
                    onToggleSelected={selectable ? () => toggleSelected(member) : undefined}
                />
            ))}
        </div>
    );

    return(
    <div className={`w-full mr-4 ` }>
            {selectedMembers.length > 0 && (
                <TeamSelectionBar members={selectedMembers} videoAvailable={rtcFeatures.videoCalls} onClear={() => setSelected(new Set())}/>
            )}
            <div className={`max-h-[calc(100vh-130px)] overflow-x-hidden overflow-y-auto ${mobileWidth || tabletWidth ? 'px-4' : 'px-6'} `}>
                <div className={'sticky top-0 z-10 py-4 bg-surface'}>
                    <TeamToolbar
                        filter={filter}
                        onFilterChange={setFilter}
                        query={query}
                        onQueryChange={setQuery}
                        prefs={prefs}
                        onPrefsChange={updatePrefs}
                    />
                </div>

                {visibleMembers.length === 0 ? (
                    <div data-testid="team-empty" className={'py-16 text-center text-sm text-ink-muted'}>
                        No team members match your filters.
                    </div>
                ) : (
                    <div className="flex flex-col gap-6 pb-6">
                        {sections.map(section => (
                            <section key={section.group ?? 'all'} className="flex flex-col gap-3">
                                {section.group && (
                                    <div data-testid="team-section" className="flex items-center gap-2">
                                        <StatusDot group={section.group}/>
                                        <span className={'text-xs font-semibold uppercase tracking-wide text-ink-muted'}>
                                            {groupLabel(section.group)}
                                        </span>
                                        <span className={'text-xs text-ink-muted'}>{section.members.length}</span>
                                        <div className={'h-px flex-1 border-t border-line'}/>
                                    </div>
                                )}
                                {renderMembers(section.members)}
                            </section>
                        ))}
                    </div>
                )}
            </div>
        </div>

    );
};

export default TeamList;
